import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, getSelectedContext } from "@/lib/api";
import { midpoint, skewFrom } from "@/lib/qrAccept";
import type {
  QRPendingOrder,
  QRPendingOrdersResponse,
  QRAcceptPendingOrderRequest,
  QRAcceptPendingOrderResponse,
  QRRejectPendingOrderRequest,
} from "@/types/qr-orders";

const getCurrentOutletId = (): string | null => {
  const context = getSelectedContext();
  return context?.outlet?.id ?? null;
};

const requireOutletId = (): string => {
  const id = getCurrentOutletId();
  if (!id) throw new Error("No outlet selected");
  return id;
};

/** Query key prefix of the pending QR orders list (every status, every outlet). */
export const QR_PENDING_ORDERS_KEY = ["qr-pending-orders"] as const;

/** List pending QR orders. Polls every 30s as a fallback to WebSocket events.
 *  Normalizes the API response so consumers always see `{ items: QRPendingOrder[] }`
 *  regardless of whether the backend returns a bare array or a wrapped object,
 *  plus `skewMs` (server clock minus this device's) from the items' `server_time`.
 *
 *  Readable with qr_orders.accept or qr_orders.manage: pass `enabled: false`
 *  for anyone else (the endpoint would only answer 403).
 *
 *  IMPORTANT: Uses `skipForceRedirect` so a 401 from this background poll
 *  does NOT kick the user out of the app. Errors are surfaced to React Query
 *  and handled silently (the badge just doesn't update). */
/** How often the pending list is asked for while the notification socket is open (events do the rest). */
export const QR_PENDING_POLL_MS = 30_000;
/** ...and while it isn't: inside the 20 s accept window, so a dead socket can't lose an order's prompt. */
export const QR_PENDING_POLL_NO_SOCKET_MS = 10_000;

export const useQRPendingOrders = (
  status: "pending" | "all" = "pending",
  options: { enabled?: boolean; refetchInterval?: number } = {},
) => {
  const outletId = getCurrentOutletId();
  return useQuery({
    queryKey: [...QR_PENDING_ORDERS_KEY, outletId, status],
    queryFn: async (): Promise<QRPendingOrdersResponse> => {
      const requestedAt = Date.now();
      let headerTime: string | null = null;
      const raw = await api.get<unknown>(
        `/api/v1/outlets/${outletId}/qr-pending-orders?status=${status}`,
        {
          skipForceRedirect: true,
          // Readable cross-origin only if the API exposes it (Access-Control-Expose-Headers).
          onResponse: (response) => {
            headerTime = response.headers?.get("X-Server-Time") ?? null;
          },
        }
      );
      const receivedAt = Date.now();
      // Backend may return a bare array, { items: [...] }, or { data: [...] }
      const items: QRPendingOrder[] = Array.isArray(raw)
        ? (raw as QRPendingOrder[])
        : ((raw as { items?: QRPendingOrder[]; data?: QRPendingOrder[] })?.items
            ?? (raw as { data?: QRPendingOrder[] })?.data
            ?? []);
      // Every item carries the same server_time (the list stays a bare array for Android 1.1.0);
      // an empty list has only the X-Server-Time header.
      const serverTime =
        items.find((i) => i.server_time)?.server_time ??
        (raw as { server_time?: string } | null)?.server_time ??
        headerTime;
      return { items, skewMs: skewFrom(serverTime, midpoint(requestedAt, receivedAt)), requestedAt };
    },
    enabled: !!outletId && (options.enabled ?? true),
    refetchInterval: options.refetchInterval ?? QR_PENDING_POLL_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

/** After an accept: the table's order, the floor, the kitchen and the QR list all changed. */
export function invalidateAfterQrAccept(queryClient: QueryClient, orderId?: string | null) {
  queryClient.invalidateQueries({ queryKey: QR_PENDING_ORDERS_KEY });
  queryClient.invalidateQueries({ queryKey: ["active-orders"], exact: false });
  queryClient.invalidateQueries({ queryKey: ["kds-tickets"], exact: false });
  queryClient.invalidateQueries({ queryKey: ["tables-with-areas"] });
  queryClient.invalidateQueries({ queryKey: ["tables-summary"] });
  queryClient.invalidateQueries({ queryKey: ["table-session"] });
  if (orderId) queryClient.invalidateQueries({ queryKey: ["order", orderId] });
}

/**
 * Accept a QR order (POST .../accept): its items join the table's order and go
 * to the kitchen, and the caller becomes the table's waiter. `items` is Modify &
 * accept (qr_orders.manage only). The paper width is left to the outlet's Print
 * Preferences, as Send KOT (/confirm) does. The response carries the KOT print
 * payloads; the caller prints them (printAcceptKots).
 *
 * 409 QR_ORDER_ALREADY_RESOLVED (someone, or the auto-accept, was first),
 * 409 SESSION_ENDED, 409 ORDER_CLOSED, 403 FORBIDDEN.
 */
export const useAcceptQRPendingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: QRAcceptPendingOrderRequest }) => {
      const outletId = requireOutletId();
      return api.post<QRAcceptPendingOrderResponse>(
        `/api/v1/outlets/${outletId}/qr-pending-orders/${id}/accept`,
        {
          ...(data?.items ? { items: data.items } : {}),
          ...(data?.print_remotely ? { print_remotely: true } : {}),
        },
      );
    },
    onSuccess: (result) => invalidateAfterQrAccept(queryClient, result?.order_id),
    // Already resolved elsewhere: the list is stale either way. Not awaited, so
    // the caller hears about the failure before the list comes back.
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: QR_PENDING_ORDERS_KEY });
    },
  });
};

export const useRejectQRPendingOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: QRRejectPendingOrderRequest }) => {
      const outletId = requireOutletId();
      return api.post(
        `/api/v1/outlets/${outletId}/qr-pending-orders/${id}/reject`,
        data
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QR_PENDING_ORDERS_KEY });
    },
  });
};
